/**
 * The Dashplan day timeline.
 *
 * Layout: a fixed hour gutter on the left, blocks absolutely positioned by
 * (startMinute - windowStart) * PX_PER_MIN. Overlapping blocks are laid out
 * side by side in columns so neither is hidden.
 *
 * Blocks are dragged to reschedule and resized from either edge. The pointer
 * handling lives in `use-block-gesture.ts`, which serves mouse and touch from
 * one code path and only writes on release — a drag is one mutation, not one
 * per pixel.
 */
import { useMemo, useRef } from "react";

import {
  formatMinute,
  formatEndMinute,
  formatDuration,
  snapMinute,
} from "@/lib/dates";
import { useBlockGesture, type Span } from "./use-block-gesture";
import {
  PX_PER_MIN,
  DEFAULT_START_HOUR,
  DEFAULT_END_HOUR,
} from "./constants";
import { NowMarker } from "./now-marker";
import { DashplanBlock, type TimelineEntry } from "./block";

interface TimelineProps {
  entries: TimelineEntry[];
  /** Null when the shown day is not today. */
  nowMinute: number | null;
  /** One callback for both gestures: a resize is a move that changed length. */
  onMove: (id: string, span: Span) => void;
  onSelect: (entry: TimelineEntry) => void;
  onToggleStatus: (entry: TimelineEntry) => void;
  onCreateAt: (startMinute: number) => void;
}

/** The minimum an entry needs for layout — lets tests exercise it directly. */
export interface LayoutInput {
  _id: string;
  startMinute: number;
  durationMinutes: number;
}

/**
 * Assign overlapping entries to columns.
 *
 * Sweeps in start order, placing each entry in the first column whose last
 * block has already ended. `columns` is the width divisor for a whole cluster,
 * so two overlapping blocks each take half the width even if a third,
 * unrelated block later in the day sits alone.
 */
export function layout<T extends LayoutInput>(entries: T[]) {
  const sorted = [...entries].sort((a, b) => a.startMinute - b.startMinute);
  const placed: Array<{
    entry: T;
    column: number;
    clusterId: number;
  }> = [];

  let clusterId = 0;
  let clusterEnd = -1;
  let columnEnds: number[] = [];

  for (const entry of sorted) {
    const end = entry.startMinute + entry.durationMinutes;

    // A gap with nothing overlapping starts a fresh cluster.
    if (entry.startMinute >= clusterEnd) {
      clusterId += 1;
      columnEnds = [];
    }

    let column = columnEnds.findIndex((colEnd) => colEnd <= entry.startMinute);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(end);
    } else {
      columnEnds[column] = end;
    }

    clusterEnd = Math.max(clusterEnd, end);
    placed.push({ entry, column, clusterId });
  }

  // Width per cluster is set by the widest point in that cluster.
  const clusterWidth = new Map<number, number>();
  for (const item of placed) {
    clusterWidth.set(
      item.clusterId,
      Math.max(clusterWidth.get(item.clusterId) ?? 1, item.column + 1),
    );
  }

  return placed.map((item) => ({
    ...item,
    columns: clusterWidth.get(item.clusterId) ?? 1,
  }));
}

export function Timeline({
  entries,
  nowMinute,
  onMove,
  onSelect,
  onToggleStatus,
  onCreateAt,
}: TimelineProps) {
  const surfaceRef = useRef<HTMLDivElement>(null);
  const { active, begin, nudge, shouldSuppressClick } = useBlockGesture({
    pxPerMinute: PX_PER_MIN,
    onCommit: onMove,
  });

  // The visible window stretches to include anything scheduled outside the
  // default 06:00-23:00, so an early flight or a late drive is never clipped.
  const { windowStart, windowEnd } = useMemo(() => {
    let start = DEFAULT_START_HOUR * 60;
    let end = DEFAULT_END_HOUR * 60;
    for (const entry of entries) {
      start = Math.min(start, Math.floor(entry.startMinute / 60) * 60);
      end = Math.max(
        end,
        Math.ceil((entry.startMinute + entry.durationMinutes) / 60) * 60,
      );
    }
    if (nowMinute !== null) {
      start = Math.min(start, Math.floor(nowMinute / 60) * 60);
      end = Math.max(end, Math.ceil(nowMinute / 60) * 60);
    }
    return { windowStart: Math.max(0, start), windowEnd: Math.min(1440, end) };
  }, [entries, nowMinute]);

  const hours = useMemo(() => {
    const out: number[] = [];
    for (let h = windowStart / 60; h <= windowEnd / 60; h += 1) out.push(h);
    return out;
  }, [windowStart, windowEnd]);

  const height = (windowEnd - windowStart) * PX_PER_MIN;
  const placed = useMemo(() => layout(entries), [entries]);

  /** Convert a clientY into a minute of the day. */
  const minuteAt = (clientY: number): number => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect) return windowStart;
    return snapMinute(windowStart + (clientY - rect.top) / PX_PER_MIN, 5);
  };

  return (
    <div className="flex gap-2">
      {/* Hour gutter */}
      <div className="relative shrink-0 w-12" style={{ height }}>
        {hours.map((hour) => (
          <div
            key={hour}
            className="absolute right-2 -translate-y-1/2 text-[11px] font-mono text-muted-foreground tabular-nums"
            style={{ top: (hour * 60 - windowStart) * PX_PER_MIN }}
          >
            {String(hour).padStart(2, "0")}:00
          </div>
        ))}
      </div>

      {/* Blocks surface */}
      <div
        ref={surfaceRef}
        className="relative flex-1 min-w-0 rounded-lg border border-border bg-card/30"
        style={{ height }}
        onDoubleClick={(event) => {
          // Double-click empty space to add a block at that time.
          if (event.target === event.currentTarget) {
            onCreateAt(minuteAt(event.clientY));
          }
        }}
      >
        {/* Hour rules */}
        {hours.map((hour) => (
          <div
            key={hour}
            className="absolute inset-x-0 border-t border-border/50 pointer-events-none"
            style={{ top: (hour * 60 - windowStart) * PX_PER_MIN }}
          />
        ))}

        {placed.map(({ entry, column, columns }) => {
          const isDragging = active?.id === entry._id;
          // While a gesture runs the block follows the pointer; the write
          // happens on release, so `entry` is still the last saved state.
          const span: Span = isDragging
            ? { startMinute: active.startMinute, durationMinutes: active.durationMinutes }
            : {
                startMinute: entry.startMinute,
                durationMinutes: entry.durationMinutes,
              };
          const origin: Span = {
            startMinute: entry.startMinute,
            durationMinutes: entry.durationMinutes,
          };

          return (
            <DashplanBlock
              key={entry._id}
              entry={entry}
              top={(span.startMinute - windowStart) * PX_PER_MIN}
              height={span.durationMinutes * PX_PER_MIN}
              column={column}
              columns={columns}
              isDragging={isDragging}
              displayStart={span.startMinute}
              displayDuration={span.durationMinutes}
              onGesture={(mode, event) => begin(entry._id, origin, mode, event)}
              onNudge={(mode, steps) => nudge(entry._id, origin, mode, steps)}
              // A drag ends in a click on the body. Opening the editor on top
              // of the block that was just moved is never what was meant.
              onSelect={() => {
                if (!shouldSuppressClick(entry._id)) onSelect(entry);
              }}
              onToggleStatus={() => onToggleStatus(entry)}
            />
          );
        })}

        {nowMinute !== null &&
          nowMinute >= windowStart &&
          nowMinute <= windowEnd && (
            <NowMarker minute={nowMinute} startMinute={windowStart} />
          )}

        {entries.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-sm text-muted-foreground text-center px-6">
              Nothing planned yet.
              <br />
              <span className="text-xs">
                Add a block, apply a template, or copy yesterday.
              </span>
            </p>
          </div>
        )}

        {active && (
          <div className="absolute top-2 right-2 z-40 px-2 py-1 rounded-md bg-popover border border-border text-xs font-mono shadow-lg tabular-nums">
            {formatMinute(active.startMinute)}–
            {formatEndMinute(active.startMinute + active.durationMinutes)}
            <span className="ml-1.5 text-muted-foreground">
              {formatDuration(active.durationMinutes)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

